import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { Brain, Check, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

import PageHeader from '../../layouts/PageHeader';
import { useCapabilities } from '../../../context/CapabilityContext';
import { profileVerificationPresentation } from './profileVerificationPresentation';
import {
    listMemories,
    listProposals,
    confirmProposal,
    rejectProposal,
    buildCorrectionClaim,
    correctMemory,
    forgetMemory,
    getAgentProfile,
    confirmAgentProfile,
    rebuildAgentProfile,
} from '../../../services/api/agentMemoryApi';

/**
 * Vad Finn vet — the Phase 4 trust surface (plan §8.8): every non-authoritative thing the
 * platform remembers, with value, scope, source, status, dates and edit/correct/forget — plus
 * the verified company facts from the confirmed profile, read-only (those are corrected at
 * the source, in company settings, not here).
 *
 * A product page, not a compliance page: the sections demonstrate WHY the assistant improves
 * over time, and every learned row can be removed by the user.
 */

const KIND_LABELS = {
    fact: 'Verksamhetsfakta',
    preference: 'Preferenser',
    pattern: 'Mönster',
    correction: 'Rättelser',
};
const KIND_ORDER = ['fact', 'preference', 'pattern', 'correction'];

const STATUS_BADGE = {
    confirmed: { bg: 'success', label: 'Bekräftad' },
    inferred: { bg: 'secondary', label: 'Föreslagen' },
    contradicted: { bg: 'warning', label: 'Motsagd' },
};

export default function WhatFinnKnowsPage() {
    const { companyId } = useParams();
    const { capabilities, hasLoaded } = useCapabilities();
    const isAdmin = Boolean(capabilities?.isAdmin || capabilities?.isOwner);

    const [memories, setMemories] = useState([]);
    const [proposals, setProposals] = useState([]);
    const [profile, setProfile] = useState(null);
    const [building, setBuilding] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [loading, setLoading] = useState(true);
    const [unavailable, setUnavailable] = useState(false);
    const profileVerification = profileVerificationPresentation(profile);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [mem, props] = await Promise.all([listMemories(companyId), listProposals(companyId)]);
            setMemories(mem.data?.memories ?? []);
            setProposals((props.data?.proposals ?? []).filter((p) => p.state === 'pending'));
            setUnavailable(false);
        } catch (err) {
            // 404 = the memory surface is not enabled for this deployment yet.
            setUnavailable(true);
        }
        try {
            // Drafts render too — with a badge and an admin confirm action. Before this the
            // card required a confirmed profile, but nothing in the product ever confirmed one.
            const prof = await getAgentProfile(companyId);
            setProfile(prof.data?.profile ?? null);
        } catch {
            /* no profile yet — the section simply doesn't render */
        }
        setLoading(false);
    }, [companyId]);

    /**
     * Build the profile for the first time (or refresh it against the current facts).
     *
     * Until this existed the chain had no beginning: `GET /profile` answered 404 for every
     * tenant, the card below never rendered, and "Finn knows the company" could not become true
     * however well the rest of Phase 2 worked.
     *
     * Rebuild is idempotent on the fact snapshot — an unchanged company re-serves the stored
     * version and spends nothing — so pressing it twice is safe and cheap.
     */
    const buildProfile = async () => {
        setBuilding(true);
        try {
            await rebuildAgentProfile(companyId);
            toast.success('Företagsuppgifterna är hämtade — kontrollera dem och bekräfta.');
            await load();
        } catch {
            toast.error('Kunde inte hämta företagsuppgifterna.');
        }
        setBuilding(false);
    };

    const confirmProfile = async () => {
        setConfirming(true);
        try {
            await confirmAgentProfile(companyId, profile.version);
            toast.success('Uppgifterna är bekräftade.');
            await load();
        } catch {
            toast.error('Kunde inte bekräfta uppgifterna.');
        }
        setConfirming(false);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!cancelled) await load();
        })();
        return () => {
            cancelled = true;
        };
    }, [load]);

    const decide = async (proposalId, decision) => {
        try {
            await (decision === 'confirm' ? confirmProposal : rejectProposal)(companyId, proposalId);
            toast.success(decision === 'confirm' ? 'Sparat som minne.' : 'Förslaget avvisades.');
            await load();
        } catch {
            toast.error('Kunde inte uppdatera förslaget.');
        }
    };

    const forget = async (memory) => {
        try {
            await forgetMemory(companyId, memory.id);
            toast('Borttaget — Finn använder inte längre den uppgiften.');
            await load();
        } catch {
            toast.error('Kunde inte ta bort minnet.');
        }
    };

    if (loading || !hasLoaded) {
        return (
            <div className="col-12">
                <PageHeader icon={Brain} title="Vad Finn vet" subtitle="Uppgifter assistenten har lärt sig — och dina kontroller över dem." feedbackSource="what-finn-knows" />
                <div className="modern-card"><div className="modern-card-body text-center py-5"><Spinner animation="border" size="sm" /></div></div>
            </div>
        );
    }

    return (
        <div className="col-12">
            <PageHeader
                icon={Brain}
                title="Vad Finn vet"
                subtitle="Uppgifter assistenten har lärt sig — och dina kontroller över dem."
                feedbackSource="what-finn-knows"
            />

            {unavailable ? (
                <Alert variant="light">
                    Minnesfunktionen är inte aktiverad för ert konto ännu. Uppgifter Finn lär sig kommer att
                    visas här, med möjlighet att rätta eller ta bort varje enskild uppgift.
                </Alert>
            ) : (
                <>
                    {proposals.length > 0 && (
                        <div className="modern-card mb-3">
                            <div className="modern-card-header"><strong>Väntar på ditt godkännande</strong></div>
                            <div className="modern-card-body d-flex flex-column gap-2">
                                {proposals.map((p) => (
                                    <div key={p.id} className="d-flex align-items-center justify-content-between gap-2 p-2 rounded" style={{ background: 'var(--rgba-primary-1)' }}>
                                        <span>{p.displayText}</span>
                                        <span className="d-flex gap-1">
                                            <Button size="sm" variant="outline-success" onClick={() => decide(p.id, 'confirm')} title="Spara som minne"><Check size={14} /></Button>
                                            <Button size="sm" variant="outline-secondary" onClick={() => decide(p.id, 'reject')} title="Avvisa"><X size={14} /></Button>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {KIND_ORDER.map((kind) => {
                        const rows = memories.filter((m) => m.kind === kind);
                        if (rows.length === 0) return null;
                        return (
                            <div className="modern-card mb-3" key={kind}>
                                <div className="modern-card-header"><strong>{KIND_LABELS[kind]}</strong></div>
                                <div className="modern-card-body d-flex flex-column gap-2">
                                    {rows.map((m) => (
                                        <MemoryRow key={m.id} memory={m} companyId={companyId} isAdmin={isAdmin} onForget={() => forget(m)} onChanged={load} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {memories.length === 0 && proposals.length === 0 && (
                        <Alert variant="light">
                            Finn har inte lärt sig något ännu. När du berättar något i chatten — till exempel
                            att lönen körs den 25:e — föreslår Finn att minnas det, och det du bekräftar
                            hamnar här.
                        </Alert>
                    )}
                </>
            )}

            {/* `unavailable` means the agent platform itself is unreachable — offering a button
                that can only fail would be worse than staying quiet. */}
            {!unavailable && !profile && (
                <div className="modern-card">
                    <div className="modern-card-header">
                        <strong>Företagsuppgifter</strong>{' '}
                        <span className="text-muted small">— vad Finn vet om företaget</span>
                    </div>
                    <div className="modern-card-body">
                        <p className="text-muted small mb-3">
                            Finn kan hämta företagsform, momsperiod, bokföringsmetod och räkenskapsår från era
                            registrerade uppgifter, så att han slipper fråga om sådant ni redan fyllt i. Ni får
                            granska och bekräfta uppgifterna innan han använder dem.
                        </p>
                        {isAdmin ? (
                            <Button size="sm" variant="outline-primary" disabled={building} onClick={buildProfile}>
                                {building ? 'Hämtar…' : 'Hämta företagsuppgifter'}
                            </Button>
                        ) : (
                            <span className="text-muted small">Bara ägare eller administratör kan hämta uppgifterna.</span>
                        )}
                    </div>
                </div>
            )}

            {profile && (profile.facts ?? []).length > 0 && (
                <div className="modern-card">
                    <div className="modern-card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <span>
                            <strong>Företagsuppgifter</strong>{' '}
                            <span className="text-muted small">— från era registrerade uppgifter; ändras under Inställningar</span>
                        </span>
                        {profile.status === 'confirmed' ? (
                            <span className="d-flex align-items-center gap-2">
                                <Badge bg={profileVerification.variant}>{profileVerification.label}</Badge>
                                {isAdmin && (
                                    <Button size="sm" variant="outline-secondary" disabled={building} onClick={buildProfile}>
                                        {building ? 'Uppdaterar…' : 'Uppdatera från inställningarna'}
                                    </Button>
                                )}
                            </span>
                        ) : (
                            <span className="d-flex align-items-center gap-2">
                                <Badge bg="secondary">Ej bekräftade</Badge>
                                {isAdmin && (
                                    <Button size="sm" variant="outline-success" disabled={confirming} onClick={confirmProfile}>
                                        {confirming ? 'Bekräftar…' : 'Ser rätt ut — bekräfta'}
                                    </Button>
                                )}
                            </span>
                        )}
                    </div>
                    <div className="modern-card-body">
                        <p className="text-muted small mb-3">
                            Profilen sammanställdes {new Date(profile.composedAt).toLocaleDateString('sv-SE')}.
                            {' '}Tidskänsliga uppgifter visas här som en ögonblicksbild; Finn använder bara
                            giltiga profiluppgifter och hämtar operativa värden via aktuella verktyg.
                        </p>
                        {profile.status !== 'confirmed' && (
                            <p className="text-muted small mb-3">
                                Kontrollera att uppgifterna stämmer. Först när de är bekräftade får Finn använda dem
                                som underlag i sina svar.
                            </p>
                        )}
                        <ProfileFactsView facts={profile.facts ?? []} />
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Verified-facts presentation (verify-pass finding F5): humans read labels and grouped
// sections, not key=json rows. Curated allowlist with Swedish labels + formatters; anything
// uncurated stays reachable under a technical fold instead of polluting the main view.

const FACT_SECTIONS = [
    {
        title: 'Företaget',
        keys: {
            'identity.name': { label: 'Namn' },
            'identity.legalForm': { label: 'Företagsform' },
            'identity.orgNumber': { label: 'Organisationsnummer' },
            'identity.registrationDate': { label: 'Registrerat' },
            'identity.registeredOffice': { label: 'Säte' },
            'identity.city': { label: 'Ort' },
            'identity.sni': { label: 'Bransch (SNI)', format: (v) => (v?.code ? `${v.code} — ${v.label ?? ''}` : String(v)) },
            'identity.businessPurpose': { label: 'Verksamhet' },
        },
    },
    {
        title: 'Registreringar',
        keys: {
            'registrations.vatStatus': { label: 'Moms', format: (v) => (v === 'registered' ? 'Registrerad' : v === 'exempt' ? 'Undantagen' : 'Ej registrerad') },
            'registrations.vatNumber': { label: 'Momsnummer' },
            'registrations.vatReportingPeriod': { label: 'Momsperiod', format: (v) => ({ month: 'Månadsvis', quarter: 'Kvartalsvis', annual: 'Årsvis' }[v] ?? String(v)) },
            'registrations.fTax': { label: 'F-skatt' },
            'registrations.employer': { label: 'Arbetsgivarregistrerad' },
        },
    },
    {
        title: 'Bokföring',
        keys: {
            'accounting.methodDefault': { label: 'Bokföringsmetod', format: (v) => (v === 'accrual' ? 'Faktureringsmetoden' : 'Kontantmetoden') },
            'fiscal.latestYear': { label: 'Räkenskapsår', format: (v) => (v?.year ? `${v.year} (${v.state === 'open' ? 'öppet' : 'stängt'})` : String(v)) },
            'history.verificationCount': { label: 'Verifikationer' },
            'currency.foreignCurrencyInvoicing': { label: 'Fakturerar i utländsk valuta' },
        },
    },
    {
        title: 'Personal',
        keys: {
            'payroll.activeEmployees': { label: 'Aktiva anställda' },
            'payroll.runCount': { label: 'Lönekörningar' },
        },
    },
];

function formatFactValue(value, format) {
    if (format) return format(value);
    if (value === true) return 'Ja';
    if (value === false) return 'Nej';
    if (Array.isArray(value)) return value.map((v) => (typeof v === 'object' ? v?.name ?? JSON.stringify(v) : String(v))).join(', ') || '—';
    if (value && typeof value === 'object') return JSON.stringify(value);
    return String(value ?? '—');
}

function ProfileFactsView({ facts }) {
    const visible = facts.filter((f) => f.source !== 'agents-config');
    const byKey = new Map(visible.map((f) => [f.key, f]));
    const curatedKeys = new Set(FACT_SECTIONS.flatMap((s) => Object.keys(s.keys)));
    const rest = visible.filter((f) => !curatedKeys.has(f.key));

    return (
        <>
            <div className="row">
                {FACT_SECTIONS.map((section) => {
                    const rows = Object.entries(section.keys)
                        .filter(([key]) => byKey.has(key))
                        .map(([key, spec]) => ({ key, spec, fact: byKey.get(key) }));
                    if (rows.length === 0) return null;
                    return (
                        <div className="col-md-6 mb-3" key={section.title}>
                            <div className="small fw-semibold text-muted mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}>
                                {section.title}
                            </div>
                            {rows.map(({ key, spec, fact }) => (
                                <div key={key} className="d-flex justify-content-between gap-3 py-1 border-bottom" style={{ fontSize: '0.85rem' }}>
                                    <span className="text-muted">{spec.label}</span>
                                    <span className="text-end">
                                        {formatFactValue(fact.value, spec.format)}
                                        {fact.source === 'registry-cache' && (
                                            <Badge bg="light" text="dark" className="ms-2" style={{ fontSize: '0.6rem' }}>register</Badge>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
            {rest.length > 0 && (
                <details className="mt-1">
                    <summary className="small text-muted" style={{ cursor: 'pointer' }}>Visa alla uppgifter ({rest.length} tekniska)</summary>
                    <div className="d-flex flex-column gap-1 mt-2">
                        {rest.map((f) => (
                            <div key={f.key} className="d-flex justify-content-between gap-3 small">
                                <code>{f.key}</code>
                                <span className="text-truncate" style={{ maxWidth: '50%' }}>{JSON.stringify(f.value)}</span>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </>
    );
}

function MemoryRow({ memory, companyId, isAdmin, onForget, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(memory.displayText);
    const badge = STATUS_BADGE[memory.verificationStatus] ?? STATUS_BADGE.inferred;
    const mayManage = isAdmin || memory.scope === 'user';

    const saveCorrection = async () => {
        try {
            await correctMemory(companyId, memory.id, buildCorrectionClaim(memory, draft.trim()));
            toast.success('Rättat — den nya versionen gäller från nu.');
            setEditing(false);
            await onChanged();
        } catch {
            toast.error('Kunde inte spara rättelsen.');
        }
    };

    return (
        <div className="d-flex align-items-start justify-content-between gap-2 p-2 border-bottom">
            <div className="flex-grow-1">
                {editing ? (
                    <Form.Control value={draft} onChange={(e) => setDraft(e.target.value)} size="sm" autoFocus />
                ) : (
                    <span>{memory.displayText}</span>
                )}
                <div className="small text-muted mt-1">
                    <Badge bg={badge.bg} className="me-2">{badge.label}</Badge>
                    {memory.scope === 'user' ? 'Din preferens' : 'Företagsuppgift'} · källa: {memory.source} ·{' '}
                    {new Date(memory.createdAt).toLocaleDateString('sv-SE')}
                    {memory.lastUsedAt && <> · senast använd {new Date(memory.lastUsedAt).toLocaleDateString('sv-SE')}</>}
                </div>
            </div>
            {mayManage && (
                <span className="d-flex gap-1 flex-shrink-0">
                    {editing ? (
                        <>
                            <Button size="sm" variant="outline-success" onClick={saveCorrection} disabled={!draft.trim()} title="Spara"><Check size={14} /></Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(false)} title="Avbryt"><X size={14} /></Button>
                        </>
                    ) : (
                        <>
                            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(true)} title="Rätta"><Pencil size={14} /></Button>
                            <Button size="sm" variant="outline-danger" onClick={onForget} title="Ta bort"><Trash2 size={14} /></Button>
                        </>
                    )}
                </span>
            )}
        </div>
    );
}
